<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <!-- 顶部操作区 -->
      <div class="table-operations">
        <a-button type="primary" @click="handleCreate">
          <a-icon type="plus" />新增角色
        </a-button>
        <a-button @click="refreshList" :loading="loading">
          <a-icon type="reload" />刷新
        </a-button>
        <a-button @click="handleExport">
          <a-icon type="download" />导出
        </a-button>
      </div>

      <!-- 搜索框 -->
      <div style="margin-bottom: 16px">
        <a-input-search
          v-model="searchQuery"
          placeholder="搜索角色名称或描述"
          @search="handleSearch"
          style="width: 300px"
        />
      </div>

      <!-- 角色列表表格 -->
      <a-table 
        :loading="loading" 
        :size="tableSize" 
        rowKey="id" 
        :columns="columns" 
        :data-source="list"
        :pagination="pagination"
        @change="handleTableChange"
      >
        <!-- 头像列 -->
        <template slot="avatar" slot-scope="text">
          <a-avatar 
            v-if="text" 
            :src="text" 
            shape="square" 
            size="large"
          />
          <a-avatar v-else shape="square" size="large">
            {{ text?.charAt(0) }}
          </a-avatar>
        </template>

        <!-- 状态列 -->
        <template slot="status" slot-scope="text, record">
          <a-tag :color="text ? 'green' : 'red'">
            {{ text ? '启用' : '禁用' }}
          </a-tag>
          <a-switch 
            size="small" 
            :checked="text"
            @change="checked => handleStatusChange(record, checked)"
            style="margin-left: 8px"
          />
        </template>

        <!-- 创建时间列 -->
        <template slot="createTime" slot-scope="text">
          {{ formatDate(text) }}
        </template>

        <!-- 操作列 -->
        <template slot="operation" slot-scope="text, record">
          <a-button-group size="small">
            <a-button type="link" @click="handleEdit(record)">
              <a-icon type="edit" />编辑
            </a-button>
            <a-button type="link" @click="handleDialogueManagement(record)">
              <a-icon type="setting" />对话管理
            </a-button>
            <a-dropdown>
              <a-menu slot="overlay">
                <a-menu-item @click="handleDelete(record)" danger>
                  删除
                </a-menu-item>
              </a-menu>
              <a-button type="link">
                更多 <a-icon type="down" />
              </a-button>
            </a-dropdown>
          </a-button-group>
        </template>
      </a-table>

      <!-- 角色编辑弹窗 -->
      <a-modal
        :title="characterModal.title"
        :visible="characterModal.visible"
        :confirm-loading="characterModal.loading"
        :width="600"
        @ok="submitCharacterForm"
        @cancel="handleModalCancel"
        :destroyOnClose="true"
      >
        <a-form-model 
          ref="characterForm" 
          :model="characterForm" 
          :rules="characterRules" 
          :label-col="{ span: 4 }"
          :wrapper-col="{ span: 20 }"
        >
          <a-form-model-item label="角色名称" prop="name">
            <a-input 
              v-model="characterForm.name" 
              placeholder="请输入角色名称"
              :maxLength="50"
            />
          </a-form-model-item>

          <a-form-model-item label="角色描述" prop="description">
            <a-textarea
              v-model="characterForm.description"
              placeholder="请输入角色描述"
              :rows="3"
              :maxLength="200"
            />
          </a-form-model-item>

          <a-form-model-item label="角色头像">
            <div style="display: flex; align-items: center;">
              <a-upload
                name="avatar"
                list-type="picture-card"
                class="avatar-uploader"
                :show-upload-list="false"
                :custom-request="handleAvatarUpload"
                :before-upload="beforeAvatarUpload"
              >
                <div v-if="characterForm.avatar" class="avatar-container">
                  <img :src="characterForm.avatar" alt="avatar" style="width: 100%; height: 100%; object-fit: cover;" />
                  <div class="avatar-upload-mask">
                    <a-icon type="eye" @click.stop="previewAvatar(characterForm.avatar)" />
                    <a-icon type="delete" @click.stop="removeAvatar" />
                  </div>
                </div>
                <div v-else>
                  <a-icon :type="avatarLoading ? 'loading' : 'plus'" />
                  <div class="ant-upload-text">上传头像</div>
                </div>
              </a-upload>
              <div style="margin-left: 16px; color: #999; font-size: 12px;">
                建议尺寸：200×200px<br/>
                支持JPG、PNG格式
              </div>
            </div>
          </a-form-model-item>

          <a-form-model-item label="排序">
            <a-input-number 
              v-model="characterForm.sortOrder" 
              :min="0" 
              :max="999"
              style="width: 100px"
            />
          </a-form-model-item>

          <a-form-model-item label="状态">
            <a-switch
              v-model="characterForm.active"
              checked-children="启用"
              un-checked-children="禁用"
            />
          </a-form-model-item>
        </a-form-model>
      </a-modal>

      <!-- 头像预览 -->
      <a-modal :visible="previewVisible" :footer="null" @cancel="previewVisible = false">
        <img alt="avatar" style="width: 100%" :src="previewImage" />
      </a-modal>

      <!-- 对话管理弹窗 -->
      <dialogue-management 
        ref="dialogueManagement"
        :visible="dialogueModal.visible"
        :character-id="dialogueModal.characterId"
        :character-name="dialogueModal.characterName"
        @close="dialogueModal.visible = false"
      />
    </a-card>
  </page-header-wrapper>
</template>

<script>
import { listCharacter, addCharacter, updateCharacter, deleteCharacter, updateCharacterStatus } from '@/api/video/character'
import DialogueManagement from './DialogueManagement.vue'

export default {
  name: 'CharacterManagement',
  components: {
    DialogueManagement
  },
  data() {
    return {
      // 搜索查询
      searchQuery: '',
      
      // 表格数据
      list: [],
      loading: false,
      
      // 分页
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: total => `共 ${total} 条`,
        pageSizeOptions: ['10', '20', '50', '100']
      },
      
      // 表格大小
      tableSize: 'default',
      
      // 表格列定义
      columns: [
        { 
          title: 'ID', 
          dataIndex: 'id', 
          key: 'id',
          width: 240,
          align: 'center'
        },
        { 
          title: '角色名称', 
          dataIndex: 'name', 
          key: 'name',
          ellipsis: true
        },
        { 
          title: '描述', 
          dataIndex: 'description', 
          key: 'description',
          ellipsis: true
        },
        { 
          title: '头像', 
          dataIndex: 'avatar', 
          key: 'avatar',
          width: 80,
          align: 'center',
          scopedSlots: { customRender: 'avatar' }
        },
        { 
          title: '状态', 
          dataIndex: 'active', 
          key: 'status',
          width: 120,
          align: 'center',
          scopedSlots: { customRender: 'status' }
        },
        { 
          title: '排序', 
          dataIndex: 'sortOrder', 
          key: 'sortOrder',
          width: 80,
          align: 'center'
        },
        { 
          title: '创建时间', 
          dataIndex: 'createTime', 
          key: 'createTime',
          width: 160,
          align: 'center',
          scopedSlots: { customRender: 'createTime' }
        },
        { 
          title: '操作', 
          key: 'operation', 
          width: 250,
          align: 'center',
          fixed: 'right',
          scopedSlots: { customRender: 'operation' }
        }
      ],
      
      // 角色编辑弹窗
      characterModal: {
        visible: false,
        title: '新增角色',
        loading: false,
        mode: 'add'
      },
      
      // 角色表单
      characterForm: {
        id: null,
        name: '',
        description: '',
        avatar: '',
        sortOrder: 0,
        active: true
      },
      
      // 表单验证规则
      characterRules: {
        name: [
          { required: true, message: '请输入角色名称', trigger: 'blur' },
          { min: 1, max: 50, message: '长度在1到50个字符', trigger: 'blur' }
        ],
        description: [
          { max: 200, message: '长度不能超过200个字符', trigger: 'blur' }
        ]
      },
      
      // 对话管理弹窗
      dialogueModal: {
        visible: false,
        characterId: null,
        characterName: ''
      },
      
      // 头像上传
      avatarLoading: false,
      previewVisible: false,
      previewImage: ''
    }
  },
  created() {
    this.getList()
  },
  methods: {
    // 获取角色列表
    async getList() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pagination.current,
          pageSize: this.pagination.pageSize,
          search: this.searchQuery
        }
        
        const response = await listCharacter(params)
        const data = response.data || response
        
        this.list = data.rows || data.list || data.records || data
        this.pagination.total = data.total || 0
        
        if (data.current) {
          this.pagination.current = data.current
        }
        if (data.pageSize) {
          this.pagination.pageSize = data.pageSize
        }
      } catch (error) {
        console.error('获取角色列表失败:', error)
        this.$message.error('获取角色列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    
    // 刷新列表
    refreshList() {
      this.searchQuery = ''
      this.pagination.current = 1
      this.getList()
    },
    
    // 表格分页/排序变化
    handleTableChange(pagination, filters, sorter) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.getList()
    },
    
    // 导出
    handleExport() {
      this.$message.info('导出功能待实现')
    },
    
    // 创建角色
    handleCreate() {
      this.characterModal.mode = 'add'
      this.characterModal.title = '新增角色'
      this.resetCharacterForm()
      this.characterModal.visible = true
    },
    
    // 编辑角色
    handleEdit(record) {
      this.characterModal.mode = 'edit'
      this.characterModal.title = '编辑角色'
      this.characterForm = {
        id: record.id,
        name: record.name,
        description: record.description || '',
        avatar: record.avatar || '',
        sortOrder: record.sortOrder || 0,
        active: record.active
      }
      this.characterModal.visible = true
    },
    
    // 对话管理
    handleDialogueManagement(record) {
      this.dialogueModal.characterId = record.id
      this.dialogueModal.characterName = record.name
      this.dialogueModal.visible = true
      
      // 在下一个tick中初始化对话管理组件
      this.$nextTick(() => {
        if (this.$refs.dialogueManagement) {
          this.$refs.dialogueManagement.init()
        }
      })
    },
    
    // 切换状态
    async handleStatusChange(record, checked) {
      try {
        await updateCharacterStatus(record.id, checked)
        record.active = checked
        this.$message.success(checked ? '已启用' : '已禁用')
      } catch (error) {
        console.error('切换状态失败:', error)
        this.$message.error('操作失败')
      }
    },
    
    // 删除角色
    handleDelete(record) {
      this.$confirm({
        title: '警告',
        content: `确定删除角色 "${record.name}" 吗？删除后无法恢复。`,
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
          try {
            await deleteCharacter(record.id)
            this.$message.success('删除成功')
            this.getList()
          } catch (error) {
            console.error('删除角色失败:', error)
            this.$message.error('删除失败')
          }
        }
      })
    },
    
    // 头像上传前检查
    beforeAvatarUpload(file) {
      const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.name)
      const isLt2M = file.size / 1024 / 1024 < 2
      
      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过2MB!')
        return false
      }
      
      return true
    },
    
    // 处理头像上传
    async handleAvatarUpload({ file, onSuccess, onError }) {
      this.avatarLoading = true
      try {
        // 这里应该是实际的上传API调用
        // 假设API返回 { data: { url: '图片地址' } }
        const mockUrl = URL.createObjectURL(file)
        
        // 模拟API延迟
        setTimeout(() => {
          this.characterForm.avatar = mockUrl
          onSuccess({ url: mockUrl }, file)
          this.avatarLoading = false
        }, 1000)
      } catch (error) {
        onError(error)
        this.avatarLoading = false
        this.$message.error('头像上传失败')
      }
    },
    
    // 预览头像
    previewAvatar(url) {
      this.previewImage = url
      this.previewVisible = true
    },
    
    // 移除头像
    removeAvatar() {
      this.characterForm.avatar = ''
    },
    
    // 重置表单
    resetCharacterForm() {
      this.characterForm = {
        id: null,
        name: '',
        description: '',
        avatar: '',
        sortOrder: 0,
        active: true
      }
      
      if (this.$refs.characterForm) {
        this.$refs.characterForm.resetFields()
      }
    },
    
    // 提交角色表单
    submitCharacterForm() {
      this.$refs.characterForm.validate(async (valid) => {
        if (!valid) return
        
        this.characterModal.loading = true
        try {
          if (this.characterModal.mode === 'add') {
            await addCharacter(this.characterForm)
            this.$message.success('创建成功')
          } else {
            await updateCharacter(this.characterForm)
            this.$message.success('更新成功')
          }
          
          this.characterModal.visible = false
          this.getList()
        } catch (error) {
          console.error('保存角色失败:', error)
          this.$message.error(error.message || '保存失败')
        } finally {
          this.characterModal.loading = false
        }
      })
    },
    
    // 弹窗取消
    handleModalCancel() {
      this.characterModal.visible = false
      this.resetCharacterForm()
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-')
    }
  }
}
</script>

<style scoped>
.table-operations {
  margin-bottom: 16px;
}

.table-operations > button {
  margin-right: 8px;
}

.avatar-uploader >>> .ant-upload {
  width: 104px;
  height: 104px;
}

.avatar-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.avatar-upload-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.avatar-container:hover .avatar-upload-mask {
  opacity: 1;
}

.avatar-upload-mask > * {
  color: white;
  font-size: 18px;
  margin: 0 8px;
  cursor: pointer;
}
</style>